import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ensureDLToken } from "@/lib/dlToken";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Clock3, RefreshCw, DollarSign, Check, RotateCcw, Truck, FileText, Eye, FileDown, ChevronRight } from "lucide-react";
import { fmtDistance, fmtEta } from "@/lib/units";
import { makeScopeFilter, scopeRange } from "@/lib/scope";
import StatsScopeToggle from "@/components/StatsScopeToggle";
import { toast } from "sonner";

/**
 * Format the actual driving duration (minutes → "1h 23m" / "45m" / "5s+").
 * Used by the Duration Time column so operators can see how long a route
 * actually took from Start → Finish.
 */
function fmtActualDuration(min) {
  if (min == null || min < 0) return "—";
  if (min < 1) return "<1m";
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/**
 * Return the ISO Monday (00:00 UTC) for the given ISO-string date.
 * Used to group routes into Mon-Sun buckets for the driver pay resume.
 */
function isoMondayOf(iso) {
  const d = new Date(iso);
  const day = d.getUTCDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const diff = day === 0 ? -6 : 1 - day; // shift back to Monday
  const mon = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + diff));
  return mon;
}

/** "Feb 12 – Feb 18, 2026" style label from a Monday Date. */
function fmtWeekRange(monday) {
  const sun = new Date(monday);
  sun.setUTCDate(sun.getUTCDate() + 6);
  const opts = { month: "short", day: "numeric", timeZone: "UTC" };
  const mStr = monday.toLocaleDateString("en-US", opts);
  const sStr = sun.toLocaleDateString("en-US", opts);
  return `${mStr} – ${sStr}, ${monday.getUTCFullYear()}`;
}

/** Group routes by (Monday-of-week, driver) and sum driver_pay. */
function buildPayResume(rows) {
  const byWeek = new Map(); // mondayISO → { monday, drivers: Map<driverId, {name, total, count}> }
  for (const r of rows) {
    if (!r.driver_id || !r.created_at) continue;
    const monday = isoMondayOf(r.created_at);
    const key = monday.toISOString().slice(0, 10);
    const pay = r.driver_pay != null ? r.driver_pay : (r.driver_pay_default || 0);
    if (!byWeek.has(key)) byWeek.set(key, { monday, drivers: new Map(), weekTotal: 0 });
    const bucket = byWeek.get(key);
    if (!bucket.drivers.has(r.driver_id)) {
      bucket.drivers.set(r.driver_id, { name: r.driver_name || "Unknown", total: 0, count: 0 });
    }
    const d = bucket.drivers.get(r.driver_id);
    d.total += pay;
    d.count += 1;
    bucket.weekTotal += pay;
  }
  return Array.from(byWeek.values())
    .map((b) => ({
      ...b,
      drivers: Array.from(b.drivers.values()).sort((a, b) => b.total - a.total),
    }))
    .sort((a, b) => b.monday - a.monday); // most recent week first
}

/**
 * /admin/timesheet (and /dispatch/timesheet) — driver-pay management.
 *
 * One row per route. The dispatcher fills in `driver_pay` per route; the
 * Routes Profit chart on the Reports page uses this value (or $0 if blank)
 * when computing profit per route.
 */
export default function AdminTimeSheet() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default to "week" per the spec — Day / Week / Month / YTD pill filters
  // the table + summary tiles by route creation date.
  const [scope, setScope] = useState("week");
  // Search filters (applied on top of the scope pill).
  const [filterDriverId, setFilterDriverId] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterRouteName, setFilterRouteName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/timesheets");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Filter routes by the scope pill FIRST (so the driver dropdown / date
  // pickers only surface options that make sense inside the chosen window),
  // then narrow further by the explicit search filters.
  const scopedRows = useMemo(
    () => rows.filter(makeScopeFilter(scope)),
    [rows, scope],
  );

  // Unique drivers surfaced from the currently-scoped rows so the dropdown
  // never shows stale drivers who have no routes in this window.
  const driverOptions = useMemo(() => {
    const map = new Map();
    for (const r of scopedRows) {
      if (r.driver_id && !map.has(r.driver_id)) {
        map.set(r.driver_id, r.driver_name || "Unknown");
      }
    }
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [scopedRows]);

  const filteredRows = useMemo(() => {
    const nameQ = filterRouteName.trim().toLowerCase();
    // Interpret date filter inputs as local YYYY-MM-DD and compare against
    // the route's `created_at` (also treated as YYYY-MM-DD) so a same-day
    // filter naturally includes routes created at any time of day.
    return scopedRows.filter((r) => {
      if (filterDriverId !== "all" && r.driver_id !== filterDriverId) return false;
      if (nameQ && !((r.label || "").toLowerCase().includes(nameQ))) return false;
      if (filterDateFrom || filterDateTo) {
        const day = (r.created_at || "").slice(0, 10);
        if (!day) return false;
        if (filterDateFrom && day < filterDateFrom) return false;
        if (filterDateTo && day > filterDateTo) return false;
      }
      return true;
    });
  }, [scopedRows, filterDriverId, filterDateFrom, filterDateTo, filterRouteName]);

  const filtersActive = filterDriverId !== "all" || !!filterDateFrom || !!filterDateTo || !!filterRouteName.trim();
  const clearFilters = () => {
    setFilterDriverId("all");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterRouteName("");
  };

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        acc.revenue += r.revenue || 0;
        const cost = r.driver_pay != null ? r.driver_pay : (r.driver_pay_default || 0);
        acc.cost += cost;
        acc.profit += r.profit || 0;
        acc.copayCollected += r.copay_collected_total || 0;
        acc.copayDeclined += r.copay_declined_count || 0;
        acc.copayConfirmedRoutes += (r.copay_confirmed_by_manager && (r.copay_collected_total || 0) > 0) ? 1 : 0;
        acc.copayCashRoutes += (r.copay_collected_total || 0) > 0 ? 1 : 0;
        return acc;
      },
      { revenue: 0, cost: 0, profit: 0, copayCollected: 0, copayDeclined: 0, copayConfirmedRoutes: 0, copayCashRoutes: 0 },
    );
  }, [filteredRows]);

  // Per-driver, per-week pay totals. IMPORTANT: the buckets are computed
  // from ALL loaded rows (not `filteredRows`), then whole weeks are kept
  // when the ISO week [Mon → Sun] OVERLAPS the pill window. This is the
  // operator-mandated behavior: "no matter if this period combines days
  // of two different months" — a week that straddles a month boundary
  // still shows its FULL Mon-Sun total instead of being chopped at the
  // month edge (previous behavior when we built the resume from the
  // month-scoped rows).
  //
  // The search filters (driver / date-range / route name) still apply per
  // route so a "just this driver" filter drills into the same weeks.
  const payResume = useMemo(() => {
    const nameQ = filterRouteName.trim().toLowerCase();
    const rowsForResume = rows.filter((r) => {
      if (filterDriverId !== "all" && r.driver_id !== filterDriverId) return false;
      if (nameQ && !((r.label || "").toLowerCase().includes(nameQ))) return false;
      if (filterDateFrom || filterDateTo) {
        const day = (r.created_at || "").slice(0, 10);
        if (!day) return false;
        if (filterDateFrom && day < filterDateFrom) return false;
        if (filterDateTo && day > filterDateTo) return false;
      }
      return true;
    });
    const buckets = buildPayResume(rowsForResume);
    // Scope-window filter at the WEEK level. A week is included if its
    // [monday, sunday] range overlaps the scope window. `scopeRange`
    // returns `{start: Date|null, end: Date|null}` — a null bound means
    // "unbounded" (e.g. week / current_month / ytd all have `end=null`).
    const { start, end } = scopeRange(scope);
    if (!start && !end) return buckets;
    return buckets.filter((b) => {
      const monMs = b.monday.getTime();
      const sunMs = monMs + 6 * 86400000; // full 7-day span in ms
      if (start && sunMs < start.getTime()) return false;
      if (end && monMs >= end.getTime()) return false;
      return true;
    });
  }, [rows, scope, filterDriverId, filterDateFrom, filterDateTo, filterRouteName]);

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="timesheet-page">
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] uppercase tracking-[0.18em] font-semibold mb-2">
            <Clock3 className="h-3 w-3" /> TimeSheet
          </div>
          <h1 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">Driver TimeSheet</h1>
          <p className="text-muted-foreground mt-1.5 max-w-2xl">
            One row per route. Set the per-route driver pay — it feeds directly into the <strong>Routes Profit</strong> chart on the Reports page. New routes start at <span className="font-mono">$0.00</span> until a Super Admin sets the amount.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Generate1099Dialog />
          <Button variant="outline" onClick={load} className="rounded-full" data-testid="timesheet-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </header>

      {/* Date scope pill — default Week. Filters table + summary tiles. */}
      <div className="flex items-center justify-between mb-3" data-testid="timesheet-scope-row">
        <span className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground">Date scope</span>
        <StatsScopeToggle value={scope} onChange={setScope} />
      </div>

      {/* Search filters — driver / date range / route name. Applied on top
          of the scope pill so a dispatcher can zoom into "this driver's
          week" or "every Route named 'Morning' in Feb". */}
      <Card className="p-3 mb-4 border-border" data-testid="timesheet-filters">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-1 block">Driver</label>
            <Select value={filterDriverId} onValueChange={setFilterDriverId}>
              <SelectTrigger className="h-9" data-testid="timesheet-filter-driver">
                <SelectValue placeholder="All drivers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="timesheet-filter-driver-all">All drivers</SelectItem>
                {driverOptions.map((d) => (
                  <SelectItem key={d.id} value={d.id} data-testid={`timesheet-filter-driver-${d.id}`}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <label className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-1 block">Date from</label>
            <Input
              type="date"
              value={filterDateFrom}
              max={filterDateTo || undefined}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="h-9"
              data-testid="timesheet-filter-date-from"
            />
          </div>
          <div className="min-w-[150px]">
            <label className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-1 block">Date to</label>
            <Input
              type="date"
              value={filterDateTo}
              min={filterDateFrom || undefined}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="h-9"
              data-testid="timesheet-filter-date-to"
            />
          </div>
          <div className="flex-[2] min-w-[200px]">
            <label className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-1 block">Route name</label>
            <Input
              type="text"
              placeholder="Search by route name…"
              value={filterRouteName}
              onChange={(e) => setFilterRouteName(e.target.value)}
              className="h-9"
              data-testid="timesheet-filter-route-name"
            />
          </div>
          {filtersActive && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-9 shrink-0 text-xs"
              data-testid="timesheet-filter-clear"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Clear filters
            </Button>
          )}
        </div>
        {filtersActive && (
          <div className="mt-2 text-[11px] text-muted-foreground" data-testid="timesheet-filter-count">
            Showing <span className="font-mono-num font-semibold text-foreground">{filteredRows.length}</span> of <span className="font-mono-num">{scopedRows.length}</span> routes in this window
          </div>
        )}
      </Card>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
        <SummaryTile label="Routes" value={filteredRows.length.toLocaleString()} testid="ts-total-routes" />
        <SummaryTile label="Total revenue" value={`$${totals.revenue.toFixed(2)}`} tone="accent" testid="ts-total-revenue" />
        <SummaryTile label="Driver pay" value={`$${totals.cost.toFixed(2)}`} tone="amber" testid="ts-total-cost" />
        <SummaryTile label="Net profit" value={`$${totals.profit.toFixed(2)}`} tone={totals.profit >= 0 ? "accent" : "destructive"} testid="ts-total-profit" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="timesheet-table">
            <thead className="bg-secondary/40 text-muted-foreground">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold text-[11px] uppercase tracking-wider">Date</th>
                <th className="px-3 py-2 font-semibold text-[11px] uppercase tracking-wider">Route</th>
                <th className="px-3 py-2 font-semibold text-[11px] uppercase tracking-wider">Driver</th>
                <th className="px-3 py-2 font-semibold text-[11px] uppercase tracking-wider">Stops</th>
                <th className="px-3 py-2 font-semibold text-[11px] uppercase tracking-wider">Distance</th>
                <th className="px-3 py-2 font-semibold text-[11px] uppercase tracking-wider">Est. Time</th>
                <th className="px-3 py-2 font-semibold text-[11px] uppercase tracking-wider" title="Actual driving time: from Start Route → Finish Route">Duration Time</th>
                <th className="px-3 py-2 font-semibold text-[11px] uppercase tracking-wider text-right">Revenue</th>
                <th className="px-3 py-2 font-semibold text-[11px] uppercase tracking-wider text-right">Driver pay</th>
                <th className="px-3 py-2 font-semibold text-[11px] uppercase tracking-wider text-right">Profit</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && filteredRows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground" data-testid="timesheet-empty">
                  {rows.length === 0
                    ? "No routes yet. Create one in the Routes tab — it will show here automatically."
                    : filtersActive
                      ? "No routes match your filters. Try clearing them or widen the date scope above."
                      : "No routes in this date range. Try a wider scope above."}
                </td></tr>
              )}
              {filteredRows.map((r) => (
                <TimesheetRow key={r.id} row={r} onSaved={(updated) => setRows((prev) => prev.map((x) => x.id === updated.id ? updated : x))} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Driver Pay Resume — grouped by ISO week (Mon-Sun) then by driver.
          Uses the same scoped rows as the table so the pill filter above
          drives both views. */}
      <PayResumePanel resume={payResume} />
      </div>
    </div>
  );
}

function PayResumePanel({ resume }) {
  // Click-to-drill-down: only the week headers are visible by default.
  // Tapping a week toggles it open to reveal per-driver totals for that
  // pay period, keeping the panel scannable when there are many weeks.
  const [openWeek, setOpenWeek] = useState(null);
  return (
    <Card className="p-4 rounded-xl border-border bg-card/80 h-fit sticky top-6" data-testid="pay-resume-panel">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200 grid place-items-center shrink-0">
          <DollarSign className="h-4 w-4" />
        </div>
        <div>
          <div className="text-sm font-display font-semibold leading-tight">Driver Pay Resume</div>
          <div className="text-[11px] text-muted-foreground">Tap a week to see per-driver payments</div>
        </div>
      </div>
      {resume.length === 0 ? (
        <div className="text-center text-xs text-muted-foreground py-8">
          No routes in this window.
        </div>
      ) : (
        <div className="space-y-2">
          {resume.map((week) => {
            const key = week.monday.toISOString().slice(0, 10);
            const isOpen = openWeek === key;
            const driverCount = week.drivers.length;
            return (
              <div key={key} className="rounded-lg border border-border overflow-hidden" data-testid={`pay-resume-week-${key}`}>
                <button
                  type="button"
                  onClick={() => setOpenWeek(isOpen ? null : key)}
                  className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 transition-colors ${isOpen ? "bg-secondary/70" : "bg-secondary/40 hover:bg-secondary/60"}`}
                  data-testid={`pay-resume-week-toggle-${key}`}
                  aria-expanded={isOpen}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground truncate">
                        {fmtWeekRange(week.monday)}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {driverCount} {driverCount === 1 ? "driver" : "drivers"}
                      </div>
                    </div>
                  </div>
                  <div className="text-[13px] font-mono-num font-semibold shrink-0">
                    ${week.weekTotal.toFixed(2)}
                  </div>
                </button>
                {isOpen && (
                  <table className="w-full text-[12px]">
                    <thead className="bg-card">
                      <tr className="text-muted-foreground text-[10px] uppercase tracking-wider">
                        <th className="px-3 py-1.5 text-left font-semibold">Driver</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Routes</th>
                        <th className="px-3 py-1.5 text-right font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {week.drivers.map((d) => (
                        <tr key={d.name} className="border-t border-border/60">
                          <td className="px-3 py-1.5 truncate">{d.name}</td>
                          <td className="px-2 py-1.5 text-right text-muted-foreground text-[11px] font-mono-num">
                            {d.count}
                          </td>
                          <td className="px-3 py-1.5 font-mono-num text-right whitespace-nowrap font-medium">
                            ${d.total.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function SummaryTile({ label, value, tone = "primary", testid }) {
  const toneClass = tone === "accent"
    ? "bg-accent/12 text-accent"
    : tone === "amber"
    ? "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
    : tone === "destructive"
    ? "bg-destructive/10 text-destructive"
    : "bg-primary/10 text-primary";
  return (
    <Card className="p-3 rounded-xl border-border bg-card/80" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-lg grid place-items-center ${toneClass}`}>
          <DollarSign className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
          <div className="font-display font-semibold text-xl leading-none mt-0.5">{value}</div>
        </div>
      </div>
    </Card>
  );
}

function TimesheetRow({ row, onSaved }) {
  const [draft, setDraft] = useState(row.driver_pay != null ? String(row.driver_pay) : "");
  const [busy, setBusy] = useState(false);

  const dirty = (draft === "" ? null : Number(draft)) !== row.driver_pay;

  const save = async () => {
    setBusy(true);
    try {
      const num = draft === "" ? null : Number(draft);
      if (num != null && (isNaN(num) || num < 0)) {
        toast.error("Driver pay must be a non-negative number");
        return;
      }
      await api.patch(`/routes/${row.id}/driver-pay`, { driver_pay: num });
      const newCost = num != null ? num : row.driver_pay_default;
      onSaved({ ...row, driver_pay: num, profit: Number(((row.revenue || 0) - newCost).toFixed(2)) });
      toast.success("Driver pay updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => setDraft(row.driver_pay != null ? String(row.driver_pay) : "");

  const profit = row.revenue - (draft === "" ? row.driver_pay_default : Number(draft || 0));

  return (
    <tr className="border-t border-border hover:bg-secondary/20 transition-colors" data-testid={`timesheet-row-${row.id}`}>
      <td className="px-3 py-2 text-muted-foreground font-mono-num text-[12px] whitespace-nowrap">
        {row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}
      </td>
      <td className="px-3 py-2 font-medium" data-testid="ts-row-label">
        {row.label || row.id.slice(0, 8)}
        <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
          row.status === "completed" ? "bg-accent/15 text-accent"
          : row.status === "in_progress" ? "bg-[#3a86ff]/15 text-[#1f4ea3]"
          : "bg-secondary text-secondary-foreground"
        }`}>
          {(row.status || "—").replace("_", " ")}
        </span>
      </td>
      <td className="px-3 py-2" data-testid="ts-row-driver">
        {row.driver_name ? (
          <span className="inline-flex items-center gap-1">
            <Truck className="h-3 w-3 text-muted-foreground" /> {row.driver_name}
          </span>
        ) : <span className="text-muted-foreground italic">Unassigned</span>}
      </td>
      <td className="px-3 py-2 font-mono-num text-[12px]">{row.delivery_stops}{row.stops_total !== row.delivery_stops && <span className="text-muted-foreground"> / {row.stops_total}</span>}</td>
      <td className="px-3 py-2 font-mono-num text-[12px]">{row.distance_km != null ? fmtDistance(row.distance_km) : "—"}</td>
      <td className="px-3 py-2 font-mono-num text-[12px]">{row.duration_min != null ? fmtEta(row.duration_min, row.delivery_stops) : "—"}</td>
      <td className="px-3 py-2 font-mono-num text-[12px]" data-testid="ts-row-duration-actual">
        {row.actual_duration_min != null ? fmtActualDuration(row.actual_duration_min) : (
          <span className="text-muted-foreground italic" title="Route not started or not finished yet">—</span>
        )}
      </td>
      <td className="px-3 py-2 font-mono-num text-right" data-testid="ts-row-revenue">${(row.revenue || 0).toFixed(2)}</td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-1.5">
          <div className="relative w-24">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={String(row.driver_pay_default)}
              className="pl-5 h-8 text-right font-mono-num text-[12px]"
              disabled={busy}
              data-testid={`ts-row-pay-input-${row.id}`}
            />
          </div>
          {dirty && (
            <Button
              size="sm"
              onClick={save}
              disabled={busy}
              className="h-8 px-2"
              data-testid={`ts-row-save-${row.id}`}
              title="Save"
            >
              <Check className="h-3.5 w-3.5" />
            </Button>
          )}
          {dirty && !busy && (
            <button
              type="button"
              onClick={reset}
              className="text-muted-foreground hover:text-foreground p-1"
              title="Reset"
              data-testid={`ts-row-reset-${row.id}`}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {row.driver_pay == null && !dirty && (
          <div className="text-[10px] text-muted-foreground text-right mt-0.5">Not set</div>
        )}
      </td>
      <td className={`px-3 py-2 font-mono-num text-right font-semibold ${profit >= 0 ? "text-accent" : "text-destructive"}`} data-testid="ts-row-profit">
        ${profit.toFixed(2)}
      </td>
    </tr>
  );
}

// ============================================================ Generate 1099

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TY_DEFAULT = new Date().getFullYear() - 1; // last completed tax year

function Generate1099Dialog() {
  const [open, setOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [driverId, setDriverId] = useState("");
  const [dateFrom, setDateFrom] = useState(`${TY_DEFAULT}-01-01`);
  const [dateTo, setDateTo] = useState(`${TY_DEFAULT}-12-31`);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/users").then((r) => {
      const ds = (r.data || []).filter((u) => u.role === "driver");
      setDrivers(ds);
      if (ds.length && !driverId) setDriverId(ds[0].id);
    }).catch(() => {});
  }, [open, driverId]);

  const runPreview = async () => {
    if (!driverId) { toast.error("Pick a driver"); return; }
    setBusy(true);
    setPreview(null);
    try {
      const { data } = await api.get("/admin/1099/preview", {
        params: { driver_id: driverId, date_from: dateFrom, date_to: dateTo },
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    // SEC-003: short-lived download token in `?auth=` so the 7-day JWT
    // never leaks into browser history / proxy logs.
    try {
      const t = await ensureDLToken();
      const url = `${BACKEND_URL}/api/admin/1099/pdf?driver_id=${encodeURIComponent(driverId)}&date_from=${dateFrom}&date_to=${dateTo}&auth=${encodeURIComponent(t || "")}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error("Could not generate download — please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid="generate-1099-btn">
          <FileText className="h-4 w-4 mr-1.5" /> Generate 1099
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl" data-testid="1099-dialog">
        <DialogHeader>
          <DialogTitle>Generate 1099-NEC Summary</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_140px] gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Driver</label>
            <Select value={driverId} onValueChange={(v) => { setDriverId(v); setPreview(null); }}>
              <SelectTrigger data-testid="1099-driver-select"><SelectValue placeholder="Pick a driver" /></SelectTrigger>
              <SelectContent>
                {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPreview(null); }} data-testid="1099-date-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">To</label>
            <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPreview(null); }} data-testid="1099-date-to" />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-2">
          <Button onClick={runPreview} disabled={busy || !driverId} variant="outline" className="rounded-full" data-testid="1099-preview-btn">
            <Eye className="h-4 w-4 mr-1.5" /> {busy ? "Loading…" : "Preview"}
          </Button>
          <Button onClick={downloadPdf} disabled={!preview} className="rounded-full" data-testid="1099-download-btn">
            <FileDown className="h-4 w-4 mr-1.5" /> Download PDF
          </Button>
        </div>

        {preview && (
          <div className="mt-3 max-h-[420px] overflow-y-auto" data-testid="1099-preview">
            <div className="grid grid-cols-2 gap-3 text-xs mb-3">
              <Card className="p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Payer</div>
                <div className="font-medium mt-0.5">{preview.company?.name}</div>
                <div className="text-muted-foreground">{preview.company?.address || "—"}</div>
                {preview.company?.tax_id && <div className="text-muted-foreground">EIN: {preview.company.tax_id}</div>}
              </Card>
              <Card className="p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Recipient</div>
                <div className="font-medium mt-0.5">{preview.driver?.name}</div>
                <div className="text-muted-foreground">{preview.driver?.address || "—"}</div>
                {preview.driver?.ssn_last4 && <div className="text-muted-foreground">SSN xxx-xx-{preview.driver.ssn_last4}</div>}
              </Card>
            </div>

            <Card className="overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-secondary/40 text-muted-foreground">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Date</th>
                    <th className="px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Route</th>
                    <th className="px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-right">Stops</th>
                    <th className="px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-right">Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.length === 0 ? (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground" data-testid="1099-empty">No routes for this driver in the chosen range.</td></tr>
                  ) : preview.rows.map((r) => (
                    <tr key={r.route_id} className="border-t border-border">
                      <td className="px-3 py-1.5 font-mono-num text-muted-foreground">{(r.date || "").slice(0, 10)}</td>
                      <td className="px-3 py-1.5">{r.route_label}</td>
                      <td className="px-3 py-1.5 text-right font-mono-num">{r.stops}</td>
                      <td className="px-3 py-1.5 text-right font-mono-num">
                        ${r.driver_pay.toFixed(2)}
                        {r.used_default && <span className="ml-1 text-amber-600" title="Default rate — set per-route on TimeSheet">⚠</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-secondary/40">
                  <tr className="border-t-2 border-border font-semibold">
                    <td colSpan={3} className="px-3 py-2 text-right uppercase tracking-wider text-[10px]">Box 1 — Total non-employee compensation</td>
                    <td className="px-3 py-2 text-right font-mono-num text-accent" data-testid="1099-total-pay">
                      ${preview.totals.total_pay.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </Card>

            <p className="text-[10px] text-muted-foreground italic mt-2">
              Summary report — not an IRS-filed 1099-NEC. Use these numbers to populate the official form.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}




/**
 * Per-route copay cell rendered on the Timesheet table. Shows collected $
 * + declined count and — for Super Admin — a "Confirm cash received"
 * button so a manager can certify they've physically collected the cash
 * the driver took in on the route. Once confirmed, flips to a green
 * "Received ✓" pill with the manager's name + timestamp.
 */
function CopayCell({ row, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  const collected = row.copay_collected_total || 0;
  const declined = row.copay_declined_count || 0;
  const bearing = row.copay_bearing_count || 0;
  const confirmed = !!row.copay_confirmed_by_manager;
  if (bearing === 0) {
    return <span className="text-muted-foreground text-xs italic">—</span>;
  }
  const confirm = async () => {
    if (!window.confirm(`Confirm $${collected.toFixed(2)} in cash was received from the driver for route "${row.label || row.id.slice(0,8)}"?`)) return;
    setBusy(true);
    try {
      await api.post(`/routes/${row.id}/copay-confirm`);
      toast.success(`Cash received confirmed · $${collected.toFixed(2)}`);
      onConfirmed?.({ ...row, copay_confirmed_by_manager: true, copay_confirmed_at: new Date().toISOString() });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="font-mono-num text-[12px]">${collected.toFixed(2)}</div>
      {declined > 0 && (
        <span
          className="text-[10px] px-1.5 py-0.5 rounded-full bg-destructive/15 text-destructive font-semibold"
          title={`${declined} of ${bearing} copay-bearing stop(s) declined by patient`}
        >
          {declined} declined
        </span>
      )}
      {collected > 0 && (
        confirmed ? (
          <span
            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-semibold"
            title={`Confirmed by ${row.copay_confirmed_by_name || "manager"} · ${row.copay_confirmed_at ? new Date(row.copay_confirmed_at).toLocaleString() : ""}`}
            data-testid={`copay-confirmed-${row.id}`}
          >
            <Check className="h-3 w-3" /> Received
          </span>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={confirm}
            disabled={busy}
            className="h-6 px-2 text-[10px] font-semibold"
            data-testid={`copay-confirm-btn-${row.id}`}
            title="Confirm cash was received from the driver"
          >
            {busy ? "…" : "Confirm cash"}
          </Button>
        )
      )}
    </div>
  );
}
